import { useEffect } from '@teact';

import { createSignal } from '../../util/signals';
import useLastCallback from '../useLastCallback';

// Backgrounded state drives every animation the app pauses to save CPU: videos and GIFs
// (`useVideoAutoPause`), animated stickers (`AnimatedSticker`), particles, and the spinner/skeleton
// keyframes gated on `body.in-background`. It must therefore never latch on.
//
// It used to be seeded from `document.hasFocus()` and updated only by `focus`/`blur`, which latches
// in two ways:
//   - `hasFocus()` is false for a tab that is plainly visible whenever its window is not the key
//     window, so the app could boot already "backgrounded" and freeze everything on first paint;
//   - WebKit does not fire window `focus`/`blur` when the user switches tabs inside one window
//     (Chromium does), so nothing ever cleared the flag afterwards.
// Together those froze every GIF, sticker and spinner for the rest of the session in Safari.
//
// Visibility is the authoritative input — a visible tab is never in the background — and `blur`/
// `focus` only refine it while the tab stays visible, so switching apps still pauses playback.
function getIsHidden() {
  return document.visibilityState === 'hidden';
}

const [getIsInBackgroundLocal, setIsInBackground] = createSignal(getIsHidden());
export const getIsInBackground = getIsInBackgroundLocal;

function enterBackground() {
  setIsInBackground(true);
}

function handleVisibilityChange() {
  setIsInBackground(getIsHidden());
}

// `blur` is the other way this latches: WebKit does not reliably fire the matching `focus` when the
// page gets focus back, so a single blur — opening Web Inspector is enough — leaves the app paused for
// good, with no visibility change to recover from. An interaction event proves the user is on this
// document right now, so it wins over every other signal, including `visibilityState`: macOS Safari
// reports a merely *occluded* window as hidden, and gating this on visibility let that report keep the
// page frozen even while the user was clicking in it. Only ever clears the flag, so a genuinely
// backgrounded tab — which receives no pointer, key or wheel events — still stops animating.
function handleInteraction() {
  if (getIsInBackgroundLocal()) {
    setIsInBackground(false);
  }
}

// `blur` (window lost focus / app switch) refines the visibility signal by entering background;
// `focus` re-derives from `visibilityState` (a focused tab is visible, so it clears the flag).
window.addEventListener('blur', enterBackground);
window.addEventListener('focus', handleVisibilityChange);
window.addEventListener('pageshow', handleVisibilityChange);
document.addEventListener('visibilitychange', handleVisibilityChange);
document.addEventListener('pointerdown', handleInteraction, { capture: true, passive: true });
document.addEventListener('keydown', handleInteraction, { capture: true, passive: true });
document.addEventListener('wheel', handleInteraction, { capture: true, passive: true });

export default function useBackgroundMode(
  onBlur?: AnyToVoidFunction,
  onFocus?: AnyToVoidFunction,
  isDisabled = false,
) {
  const lastOnBlur = useLastCallback(onBlur);
  const lastOnFocus = useLastCallback(onFocus);

  useEffect(() => {
    if (isDisabled) {
      return undefined;
    }

    if (getIsInBackground()) {
      lastOnBlur();
    }

    return getIsInBackground.subscribe(() => {
      if (getIsInBackground()) {
        lastOnBlur();
      } else {
        lastOnFocus();
      }
    });
  }, [isDisabled, lastOnBlur, lastOnFocus]);
}

export function isBackgroundModeActive() {
  return getIsInBackground();
}
